import type { RuleSet } from 'styled-components';

import styled, { css } from 'styled-components';

import { sizeButtonStyles } from '~/components/shared/common/styles';
import { Typography } from '~/components/shared/core/typography/Typography';
import type { SizeButtonStyle } from '~/utilities/constants';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const AafSizeStyle = css<{ $btnStyle: SizeButtonStyle }>`
  margin-right: ${({ $btnStyle }) => sizeButtonStyles[$btnStyle].marginRight};
  margin-bottom: ${({ $btnStyle }) => sizeButtonStyles[$btnStyle].marginBottom};
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.14px;
  min-width: 19px;
  height: 28px;
  padding: 0 4px;
  color: ${({ theme }) => theme.components.sizeSelector.rest.color};
  border-width: 0;
  border-color: ${({ theme }) => theme.components.sizeSelector.rest.borderColor};

  &[data-disabled='true'] {
    color: ${({ theme }) => theme.components.sizeSelector.disabled.color};
    border-color: ${({ theme }) => theme.components.sizeSelector.disabled.borderColor};
  }

  &[data-selected='true'] {
    color: ${({ theme }) => theme.components.sizeSelector.selected.color};
    border-width: 0 0 2px;
    border-color: ${({ theme }) => theme.components.sizeSelector.selected.borderColor};
  }

  border-style: solid;
`;
const GstarSizeStyle = css<{ $btnStyle: SizeButtonStyle }>`
  --border-width: ${({ theme }) => theme.borders.width.xs};
  --border-width-selected: ${({ theme }) => theme.borders.width.md};
  --border-color: ${({ theme }) => theme.components.sizeSelector.rest.borderColor};
  --border-color-selected: ${({ theme }) => theme.components.sizeSelector.selected.borderColor};

  margin: 0 -1px -1px 0;
  border: 0;
  width: auto;
  height: auto;
  font-size: ${({ theme }) => theme.font.size[3]};
  font-weight: ${({ theme }) => theme.variables.font.weight.medium};
  padding: ${({ theme }) => theme.padding['2xs']};
  min-width: 48px;
  min-height: 48px;

  /* prettier-ignore */
  box-shadow: inset 0 0 0 var(--border-width) var(--border-color);
  color: ${({ theme }) => theme.components.sizeSelector.rest.color};

  hr {
    display: none;
  }

  &[data-formatted='true'] {
    min-height: 56px;
    min-width: 56px;
  }

  @media (prefers-reduced-motion: no-preference) {
    transition: box-shadow 200ms ease;
  }

  &[data-selected='true'] {
    /* prettier-ignore */
    box-shadow: inset 0 0 0 var(--border-width) var(--border-color-selected), inset 0 calc(var(--border-width-selected) * -1) 0 0 var(--border-color-selected);
    color: ${({ theme }) => theme.components.sizeSelector.selected.color};
    z-index: 1;
    font-weight: 700;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 100%;
      height: var(--border-width-selected);
      background: var(--border-color-selected);
      z-index: 2;
    }
  }

  &[data-disabled='true'] {
    background: ${({ theme }) => theme.components.sizeSelector.disabled.backgroundColor};
    position: relative;
    overflow: hidden;
    color: ${({ theme }) => theme.components.sizeSelector.disabled.color};

    hr {
      pointer-events: none;
      margin: 0;
      border: 0;
      display: block;
      position: absolute;
      width: 100%;
      height: var(--border-width);
      z-index: 1;
      bottom: 0;
      left: 0;
      transform-origin: bottom left;
      background: var(--border-color);
    }

    p {
      color: ${({ theme }) => theme.components.sizeSelector.disabled.color};
    }
  }
`;
const WahtsSizeInteractionStyle = css`
  &:not([data-disabled='true'], [data-selected='true']):hover,
  &:not([data-disabled='true'], [data-selected='true']):focus-visible {
    position: relative;
    z-index: 1;

    --border-color: ${({ theme }) => theme.colors.neutral['500']};
  }

  &:focus-visible {
    outline: none;
    border-radius: 1px;
    box-shadow: inset 0 0 0 var(--border-width) var(--border-color),
      0 0 0 1px ${({ theme }) => theme.colors.background.base},
      0 0 0 3px ${({ theme }) => theme.colors.content.primary},
      0 0 0 5px ${({ theme }) => theme.colors.blue['100']};
  }

  &[data-selected='true']:focus-visible {
    box-shadow: inset 0 0 0 var(--border-width) var(--border-color-selected),
      inset 0 calc(var(--border-width-selected) * -1) 0 0 var(--border-color-selected),
      0 0 0 1px ${({ theme }) => theme.colors.background.base},
      0 0 0 3px ${({ theme }) => theme.colors.content.primary},
      0 0 0 5px ${({ theme }) => theme.colors.blue['100']};
  }
`;

const WahtsSizeStyle = css<{ $btnStyle: SizeButtonStyle }>`
  ${GstarSizeStyle}
  ${WahtsSizeInteractionStyle}
`;

const SizeStylesConfig: Record<SiteType, RuleSet<{ $btnStyle: SizeButtonStyle }>> = {
  [SiteType.Gstar]: GstarSizeStyle,
  [SiteType.Wahts]: WahtsSizeStyle,
  [SiteType.Outlet]: GstarSizeStyle,
  [SiteType.Employeeshop]: GstarSizeStyle,
  [SiteType.Aaf]: AafSizeStyle,
};

const S = {
  Size: styled.button<{ $btnStyle: SizeButtonStyle }>`
    ${({ theme }) => SizeStylesConfig[theme.siteType]}

    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    white-space: nowrap;
    flex-direction: ${({ $btnStyle }) => sizeButtonStyles[$btnStyle].direction};
  `,

  SizeText: styled(Typography)`
    font-size: ${({ theme }) => theme.font.size[2]};
    font-weight: normal;
    color: ${({ theme }) => theme.colors.slate[600]};
    white-space: nowrap;

    ${({ theme }) =>
      getSiteStyles(theme.siteType, {
        [SiteType.Gstar]: css`
          color: ${theme.colors.slate[500]};
        `,
      })}

    ${({ theme }) =>
      getSiteStyles(theme.siteType, {
        [SiteType.Outlet]: css`
          color: ${theme.colors.neutral[300]};
        `,
      })}
  `,
};

const ShowUnavailableSizeStrikethrough: Record<SiteType, boolean> = {
  [SiteType.Aaf]: false,
  [SiteType.Employeeshop]: true,
  [SiteType.Gstar]: true,
  [SiteType.Outlet]: true,
  [SiteType.Wahts]: true,
};

type Props = {
  activatable: boolean;
  currentGridValue?: string | null;
  gridValue?: string | null;
  isSizeFormatted: boolean;
  label?: string;
  onClick: () => void;
  purchasable?: boolean | null;
  sizeButtonStyle: SizeButtonStyle;
  testId: string;
  subText?: string;
  displayValue?: string | null;
  willBeAvailable?: boolean | null;
};

export const SizeItem = ({
  activatable,
  currentGridValue,
  gridValue,
  isSizeFormatted,
  label,
  onClick,
  purchasable,
  sizeButtonStyle,
  testId,
  subText,
  displayValue,
  willBeAvailable,
}: Props) => {
  const { siteType } = useAppContext();
  const isSelected =
    activatable && gridValue === currentGridValue && (purchasable || !willBeAvailable);
  const { accessibilityLabels } = useStaticContext();
  const { availableLabel = 'available', unavailableLabel = 'unavailable' } =
    accessibilityLabels?.sizeSelectorLabelsAvailability ?? {};

  return (
    <S.Size
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={`${label}, ${!purchasable ? unavailableLabel : availableLabel}`}
      data-selected={isSelected}
      data-disabled={!purchasable}
      data-formatted={isSizeFormatted}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      data-testid={`${testId}${!purchasable ? '-disabled' : ''}${isSelected ? '-active' : ''}`}
      $btnStyle={sizeButtonStyle}
      data-cs-capture=""
    >
      {!purchasable && ShowUnavailableSizeStrikethrough[siteType] && <hr role="presentation" />}

      {displayValue}
      {isSizeFormatted && subText && (
        <S.SizeText variant="body" component="p" data-cs-capture="">
          {subText}
        </S.SizeText>
      )}
    </S.Size>
  );
};
